import {
  AppstoreOutlined,
  BlockOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import type { ResourceRule } from "@p0security/shared/types/policy/types";
import { App, Space } from "@p0security/ui";
import { PolicyEditorContext } from "components/Jit/Policy/store/RoutingRuleEditorContext";
import { getResourceFilterEntries } from "components/Jit/Policy/utils";
import { FormRow } from "components/common/FormRow";
import { PillSelect, PillSelectOption } from "components/common/PillSelect";
import { useCallback, useContext, useEffect, useState } from "react";

import { SpecificResourceEditor } from "./SpecificResourceEditor";

export type ResourceMode = "any" | "bundle" | "specific";

export const MODE_OPTIONS: PillSelectOption<ResourceMode>[] = [
  {
    icon: <GlobalOutlined />,
    label: "Any resource",
    description: "Match every resource the identity could access",
    searchText: "any resource",
    value: "any",
  },
  {
    icon: <AppstoreOutlined />,
    label: "Specific resource",
    description: "Target one integration's resources, with optional filters",
    searchText: "specific resource",
    value: "specific",
  },
  {
    icon: <BlockOutlined />,
    label: "Access bundle",
    description:
      "Let the identity request a predefined bundle of resources at once",
    searchText: "access bundle",
    value: "bundle",
  },
];

const modeFromResource = (
  resource: ResourceRule | undefined
): ResourceMode | undefined => {
  if (resource === undefined) return undefined;
  if (resource.type === "any") return "any";
  return resource.service === "bundle" ? "bundle" : "specific";
};

/** Top-level resource choice for a studio policy: any resource, a specific
 *  integration resource (with drill-down), or an access bundle. */
export const ResourceEditor: React.FC<{
  onResourceFilterEditInProgress: (editing: boolean) => void;
  /** Reports whether the user has chosen "Specific resource" but not yet an
   *  integration — the rule still holds the previous value, so saving must
   *  be blocked until the selection is completed. */
  onSelectionIncomplete: (incomplete: boolean) => void;
  /** Called when the user selects "Access bundle" — redirects to the bundle
   *  creation page instead of showing an inline editor. */
  onBundleRedirect: () => void;
  initialMode?: ResourceMode;
}> = ({
  onResourceFilterEditInProgress,
  onSelectionIncomplete,
  onBundleRedirect,
  initialMode,
}) => {
  const { resource, setResource } = useContext(PolicyEditorContext);
  const { modal } = App.useApp();

  const [mode, setMode] = useState<ResourceMode | undefined>(
    () => initialMode ?? modeFromResource(resource)
  );

  const selectionIncomplete =
    mode === "specific" && modeFromResource(resource) !== "specific";
  useEffect(() => {
    onSelectionIncomplete(selectionIncomplete);
  }, [selectionIncomplete, onSelectionIncomplete]);

  const handleSelectMode = useCallback(
    (next: ResourceMode) => {
      if (next === mode) return;
      if (next === "bundle") {
        onBundleRedirect();
        return;
      }

      const apply = () => {
        onResourceFilterEditInProgress(false);
        setMode(next);
        if (next === "any") {
          setResource({ type: "any" });
        }
        // "specific" keeps the previous rule value until an integration is
        // chosen; `onSelectionIncomplete` blocks saving in the meantime.
      };

      const hasFilters = getResourceFilterEntries(resource).length > 0;
      if (hasFilters) {
        const target = MODE_OPTIONS.find((o) => o.value === next)?.label;
        modal.confirm({
          title: "Discard resource filters?",
          content: `Switching to "${target}" removes the filters you've configured.`,
          okText: "Discard filters",
          okType: "danger",
          cancelText: "Keep editing",
          onOk: apply,
        });
        return;
      }
      apply();
    },
    [
      mode,
      modal,
      onBundleRedirect,
      onResourceFilterEditInProgress,
      resource,
      setResource,
    ]
  );

  return (
    <Space direction="vertical" size="small" className="w-full">
      <FormRow>
        <PillSelect<ResourceMode>
          options={MODE_OPTIONS}
          value={mode}
          onChange={handleSelectMode}
          placeholder="Select what this policy applies to"
        />
      </FormRow>
      {mode === "specific" && (
        <SpecificResourceEditor
          onResourceFilterEditInProgress={onResourceFilterEditInProgress}
        />
      )}
    </Space>
  );
};
